import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Alert, Stack, MenuItem, Select, FormControl,
  InputLabel, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, LinearProgress,
} from '@mui/material';
import { Refresh as RefreshIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';

const STATUS_COLORS = {
  new:         'warning',
  reviewed:    'info',
  in_progress: 'info',
  fixed:       'success',
  archived:    'default',
};

const STATUSES = ['new', 'reviewed', 'in_progress', 'fixed', 'archived'];

function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString();
}

function BugReports() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(null);   // {id, status, triage_notes, fixed_in_version}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = filter ? `/api/bug-reports?status=${filter}` : '/api/bug-reports';
      const { data } = await api.get(url);
      setReports(data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      await api.patch(`/api/bug-reports/${edit.id}`, {
        status: edit.status,
        triage_notes: edit.triage_notes,
        fixed_in_version: edit.fixed_in_version || null,
      });
      setEdit(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Update failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Bug Reports"
        subtitle="User-submitted issues from the floating report button. Triage into easy-fix-now or architectural-discuss."
        actions={
          <Stack direction="row" spacing={1}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 100 }}>Status</TableCell>
                <TableCell>Reported</TableCell>
                <TableCell>From</TableCell>
                <TableCell>Page</TableCell>
                <TableCell>Message</TableCell>
                <TableCell sx={{ width: 80 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow><TableCell colSpan={6}><em>No reports.</em></TableCell></TableRow>
              ) : reports.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      color={STATUS_COLORS[r.status] || 'default'}
                      label={r.status.toUpperCase()}
                    />
                    {r.fixed_in_version && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        v{r.fixed_in_version}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmtDate(r.created_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    {r.user_name ? (
                      <>
                        <Typography variant="body2">{r.user_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.company_name || '—'}
                        </Typography>
                      </>
                    ) : <Typography variant="caption" color="text.secondary">unknown</Typography>}
                  </TableCell>
                  <TableCell>
                    {r.page_url ? (
                      <Tooltip title={r.page_url}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block' }}>
                            {r.page_url}
                          </Typography>
                          <IconButton size="small" component="a" href={r.page_url} target="_blank" rel="noreferrer">
                            <OpenIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 460 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {r.message}
                    </Typography>
                    {r.triage_notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        Notes: {r.triage_notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => setEdit({
                      id: r.id,
                      status: r.status,
                      triage_notes: r.triage_notes || '',
                      fixed_in_version: r.fixed_in_version || '',
                    })}>
                      Triage
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!edit} onClose={() => !busy && setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Triage bug report</DialogTitle>
        <DialogContent>
          {edit && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={edit.status}
                  onChange={(e) => setEdit((s) => ({ ...s, status: e.target.value }))}
                  label="Status"
                >
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                label="Triage notes"
                value={edit.triage_notes}
                onChange={(e) => setEdit((s) => ({ ...s, triage_notes: e.target.value }))}
                fullWidth
                multiline
                minRows={3}
                placeholder="What we did, what the root cause was, links to PRs/commits, etc."
              />
              <TextField
                label="Fixed in version"
                value={edit.fixed_in_version}
                onChange={(e) => setEdit((s) => ({ ...s, fixed_in_version: e.target.value }))}
                fullWidth
                size="small"
                placeholder="e.g. 9.11.0"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)} disabled={busy}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BugReports;
